import { ColumnsType } from 'antd/es/table';
import React, { PropsWithChildren, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Team, User } from 'api/models';
import { ReactComponent as NoItemsSvg } from 'assets/svgs/no-items.svg';
import { EmptyState as EmptyStateAtom } from 'components/atoms/empty-state';
import { Table } from 'components/molecules/table';
import { RoundedCheckbox } from 'components/organisms/timesheets/drawers/components';
import { AddToProjectRecordType, ReplacementUserForTeam } from '../../types';

const StyledTable = styled(Table)`
  .ant-table-body {
    min-height: 280px;
  }

  .ant-table-placeholder {
    display: flex;
    height: 280px;

    .ant-table-cell {
      padding: 0;
    }
  }

  .ant-table-cell {
    background-color: transparent;
    border-bottom: none;
    padding: var(--spacing-03) var(--spacing-05);
  }

  .ant-table-row {
    cursor: pointer;

    &:hover,
    &.selected {
      > td {
        background-color: var(--blue-10) !important;
      }
    }

    ${RoundedCheckbox} {
      visibility: hidden;
    }

    &:hover ${RoundedCheckbox}, &.selected ${RoundedCheckbox} {
      visibility: visible;
    }
  }
`;

const EmptyState = styled(EmptyStateAtom)`
  height: 100%;
  justify-content: flex-end;
  color: var(--gray-70);
  padding: var(--spacing-05) 0;

  ${EmptyStateAtom.Message} {
    text-align: start;
  }
`;

interface Props<List> {
  primaryKey: string;
  isMultiSelect: boolean;
  entities: List[];
  selectedEntities: List[];
  updateSelectedEntities: (entities: List[]) => void;
  columns: ColumnsType<AddToProjectRecordType<List>>;
}

export const EntityListTable = <
  List extends User | Team | ReplacementUserForTeam,
>({
  primaryKey,
  isMultiSelect,
  columns,
  entities,
  selectedEntities,
  updateSelectedEntities,
}: PropsWithChildren<Props<List>>): JSX.Element => {
  const onSelectionChange = useCallback(
    (entity: List, selected: boolean) => {
      if (selected) {
        if (isMultiSelect) {
          updateSelectedEntities([...selectedEntities, entity]);
        } else {
          updateSelectedEntities([entity]);
        }
      } else if (isMultiSelect) {
        updateSelectedEntities(
          selectedEntities.filter(
            (item) => item[primaryKey] !== entity[primaryKey],
          ),
        );
      } else {
        updateSelectedEntities([]);
      }
    },
    [isMultiSelect, updateSelectedEntities, selectedEntities, primaryKey],
  );

  const isEntitySelected = (entity: List): boolean =>
    primaryKey
      ? selectedEntities
          .map((item) => item[primaryKey])
          .includes(entity[primaryKey])
      : selectedEntities.includes(entity);

  const dataSource = entities.map((entity) => ({
    key: entity[primaryKey],
    entity,
    isSelected: isEntitySelected(entity),
  }));

  return (
    <StyledTable
      columns={columns}
      dataSource={dataSource}
      scroll={{ y: 280 }}
      pagination={false}
      showSorterTooltip={false}
      locale={{
        emptyText: (
          <EmptyState
            image={<NoItemsSvg />}
            message={
              <FormattedMessage id="resource_management.no_users_or_placeholder_matched" />
            }
          />
        ),
      }}
      rowClassName={(record) => (record.isSelected ? 'selected' : '')}
      onRow={(record) => {
        return {
          onClick: () => {
            onSelectionChange(record.entity, !isEntitySelected(record.entity));
          },
        };
      }}
    />
  );
};
